import Link from 'next/link';
import { ShieldCheck, BadgeCheck, type LucideIcon } from 'lucide-react';
import { cn } from 'ui/lib/utils';

interface TrustItem {
  icon: LucideIcon;
  label: string;
}

interface UtilityLink {
  label: string;
  href: string;
}

// No site-wide shipping promise: shipping is set per listing by each seller,
// so any free-shipping claim belongs on the listing, not here.
const defaultTrustItems: TrustItem[] = [
  { icon: ShieldCheck, label: 'Buyer protection on every order' },
  { icon: BadgeCheck, label: 'Verified sellers only' },
];

const defaultUtilityLinks: UtilityLink[] = [
  // Brands + Start Selling moved up here from the category nav's
  // right-aligned group (Bill, 2026-08-21) — grouped ahead of Help.
  { label: 'Brands', href: '/brands' },
  { label: 'Start Selling', href: '/sell' },
  { label: 'Help', href: '/help' },
  { label: 'Track order', href: '/dashboard/purchases' },
  // Restored: the support email was dropped from the header (only the phone
  // survived). Support-by-email is a real contact channel for buyers/sellers.
  { label: 'support@gearfocus.com', href: 'mailto:support@gearfocus.com' },
  { label: '877-606-3504', href: 'tel:18776063504' },
];

interface TopBarProps {
  trustItems?: TrustItem[];
  utilityLinks?: UtilityLink[];
  className?: string;
}

/**
 * Ported verbatim from gearfocus-v2's marketplace TopBar. rem utilities
 * converted to px for gearfocus-web's 10px rem root.
 */
const TopBar = ({
  trustItems = defaultTrustItems,
  utilityLinks = defaultUtilityLinks,
  className,
}: TopBarProps) => {
  return (
    <div
      data-slot="top-bar"
      // Shorter on mobile so the whole header lands back on today's 144px
      // (Bill, Aug 2026 — the redesign's 25px increase does not ship). The
      // wrapper in Navbar clips to the same heights, so keep the two in step.
      className={cn(
        'h-[28px] w-full bg-dark text-[12px] text-white/80 md:h-[36px]',
        className
      )}
    >
      <div className="mx-auto flex h-full w-full max-w-default items-center justify-between gap-[16px] px-4 lg:px-[40px] xl:px-default-padding-x">
        <ul
          className="flex items-center gap-[20px]"
          style={{ listStyle: 'none', margin: 0, paddingInlineStart: 0 }}
        >
          {trustItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <li
                key={item.label}
                className={cn(
                  'flex items-center gap-[6px] whitespace-nowrap',
                  i === 1 && 'hidden sm:flex',
                  i === 2 && 'hidden lg:flex'
                )}
              >
                <Icon size={14} className="text-primary" aria-hidden="true" />
                <span>{item.label}</span>
              </li>
            );
          })}
        </ul>
        <ul
          className="hidden items-center gap-[20px] md:flex"
          style={{ listStyle: 'none', margin: 0, paddingInlineStart: 0 }}
        >
          {utilityLinks.map((link) => (
            <li key={link.label} className="whitespace-nowrap">
              <Link href={link.href} className="transition-colors hover:text-white">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TopBar;
